import {
  Box,
  Button,
  Grid,
  Menu,
  MenuItem,
  Paper,
  Stack,
  TablePagination,
  Typography,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  InputAdornment,
  TextField,
  Skeleton,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useApi } from "../../hooks/callApi/callApi";
import { useIsMobile } from "../../hooks/isMobile/isMobile";
import { useCfbd } from "../../features/cfbd/hooks/useCfbd/useCfbd";
import { useLeague } from "../../hooks/useLeague/useLeague";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import GameCard from "../../features/cfbd/components/GameCard";
import { sortGamesByStartStatus } from "../../features/cfbd/utils/game";
import {
  Game,
  SelectedGame,
  UpdateSelectedGamesRequest,
} from "../../features/cfbd/types/game";
import { Rules } from "../../features/cfbd/types/rules";
import { Team } from "../../features/cfbd/types/team";
import { User } from "../../types/user";
import { PollsResponse } from "../../features/cfbd/types/poll";
import { getTeamPollRank } from "../../features/cfbd/utils/poll";
import { getTeamRecord } from "../../features/cfbd/utils/records";
import { RecordsResponse } from "../../features/cfbd/types/records";

// Local types and helpers
const allConferences = "all";

interface AdminGamesState {
  games: Game[];
  teams: Map<number, Team>;
}

// Card skeleton used in lists
const GameCardSkeleton = () => {
  const isMobile = useIsMobile();
  return (
    <Paper elevation={1} sx={{ padding: isMobile ? 1 : 2 }}>
      <Stack spacing={1}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Skeleton variant="rectangular" width="40%" height={32} />
          <Skeleton variant="text" width={20} />
          <Skeleton variant="rectangular" width="40%" height={32} />
        </Box>
        <Skeleton variant="text" width="60%" sx={{ marginTop: "auto" }} />
      </Stack>
    </Paper>
  );
};

// Selected games skeleton component
const SelectedGamesSkeleton = ({ count }: { count: number }) => {
  const isMobile = useIsMobile();
  return (
    <Grid container spacing={isMobile ? 1.5 : 3} sx={{ marginBottom: 2 }}>
      {Array.from({ length: count }).map((_, index) => (
        <Grid item xs={6} sm={6} md={4} lg={3} key={index}>
          <GameCardSkeleton />
        </Grid>
      ))}
    </Grid>
  );
};

// Scheduled games skeleton component
const ScheduledGamesSkeleton = ({ count }: { count: number }) => {
  const isMobile = useIsMobile();
  return (
    <Grid container spacing={isMobile ? 1.5 : 3} sx={{ marginBottom: 2 }}>
      {Array.from({ length: count }).map((_, index) => (
        <Grid item xs={6} sm={6} md={4} lg={3} key={index}>
          <GameCardSkeleton />
        </Grid>
      ))}
    </Grid>
  );
};

const Admin = ({
  rules,
  leagueId,
  userEmail,
}: {
  rules: Rules;
  leagueId: string;
  userEmail?: string;
}) => {
  const callApi = useApi();
  const { getLeague, getShareCode, getSelectedGames, updateSelectedGames } =
    useLeague();
  const {
    getCurrentWeek,
    getAvailableWeeks,
    getClassifications,
    getConferences,
    getPollsAndRecords,
    getGames,
    getTeams,
  } = useCfbd();
  const { user } = useAuth0();
  const isMobile = useIsMobile();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [games, setGames] = useState<AdminGamesState | null>(null);
  const [ready, setReady] = useState(false);
  const [selectedGames, setSelectedGames] = useState<SelectedGame[]>([]);
  const [gameOfTheWeek, setGameOfTheWeek] = useState<number | undefined>(
    undefined
  );
  const [classifications, setClassifications] = useState<string[]>(["fbs"]);
  const [conferences, setConferences] = useState<string[]>([]);
  const [rowsPerPage, setRowsPerPage] = useState(isMobile ? 12 : 24);
  const [page, setPage] = useState(0);
  const [rowsPerSelectedGamesPage, setRowsPerSelectedGamesPage] = useState(
    isMobile ? 12 : 24
  );
  const [selectedGamesPage, setSelectedGamesPage] = useState(0);

  const [classification, setClassification] = useState("fbs");
  const [conference, setConference] = useState<string | null>(allConferences);
  // Season year & week selection state
  const [year, setYear] = useState<number | undefined>(
    new Date().getFullYear()
  );
  // Week selection state: default undefined until fetched
  const [week, setWeek] = useState<number | undefined>(undefined);
  // Polls and records data
  const [pollsData, setPollsData] = useState<PollsResponse | null>(null);
  const [recordsData, setRecordsData] = useState<RecordsResponse | null>(null);

  const [availableWeeks, setAvailableWeeks] = useState<number[]>([]);
  // TODO: Optionally derive current week from backend selected games response if present
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Selection validation feedback
  const [selectError, setSelectError] = useState<string | null>(null);
  // Publish workflow state (replaces prior Finish semantics)
  const [publishing, setPublishing] = useState(false);
  const [publishSuccess, setPublishSuccess] = useState<string | null>(null);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [publishConfirmOpen, setPublishConfirmOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [shareCode, setShareCode] = useState<string | null>(null);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [canEditControls, setCanEditControls] = useState(false);

  const [classificationAnchorEl, setClassificationAnchorEl] =
    useState<HTMLElement | null>(null);
  const [conferenceAnchorEl, setConferenceAnchorEl] =
    useState<HTMLElement | null>(null);
  const [weekAnchorEl, setWeekAnchorEl] = useState<HTMLElement | null>(null);
  const classificationOpen = Boolean(classificationAnchorEl);
  const conferenceOpen = Boolean(conferenceAnchorEl);
  const weekOpen = Boolean(weekAnchorEl);
  const handleClassificationClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setClassificationAnchorEl(event.currentTarget);
  };
  const handleConferenceClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setConferenceAnchorEl(event.currentTarget);
  };
  const handleWeekClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (availableWeeks.length === 0) return;
    setWeekAnchorEl(event.currentTarget);
  };
  const handleClassificationClose = () => {
    setClassificationAnchorEl(null);
  };
  const handleConferenceClose = () => setConferenceAnchorEl(null);
  const handleWeekClose = () => setWeekAnchorEl(null);
  const handleClassificationMenuItemClick = (newClassification: string) => {
    if (newClassification !== classification) {
      setClassification(newClassification);
      setConference(allConferences);
    }
    handleClassificationClose();
  };
  const handleConferenceMenuItemClick = (conference: string) => {
    setConference(conference);
    handleConferenceClose();
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeSelectedGamesPage = (event: unknown, newPage: number) => {
    setSelectedGamesPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleChangeSelectedGamesRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerSelectedGamesPage(+event.target.value);
    setSelectedGamesPage(0);
  };

  const handleRowClick = (game: Game, homeTeam: Team, awayTeam: Team) => {
    if (selectedGames.length >= rules.gameCount) return;
    // Disallow selecting finished games
    if (game.completed) {
      setSelectError("You cannot select finished games.");
      setTimeout(() => setSelectError(null), 3000);
      return;
    }
    if (!selectedGames.find((g) => g.game.id === game.id)) {
      setSelectedGames((prev) => [...prev, { game, homeTeam, awayTeam }]);
    }
  };

  const handleSelectedGamesRowClick = (game: SelectedGame) => {
    if (ready) return;
    setSelectedGames(selectedGames.filter((g) => g.game.id !== game.game.id));
  };

  const handleGameOfTheWeekClick = (game: SelectedGame) => {
    if (ready) return;
    const gameId = game.game.id;
    setGameOfTheWeek(gameOfTheWeek === gameId ? undefined : gameId);
  };

  const handleInviteClick = async () => {
    setInviteLoading(true);
    setInviteError(null);
    setShareCode(null);
    setInviteOpen(true);

    const response = await getShareCode(leagueId);
    if (response instanceof Error) {
      setInviteError(response.message);
    } else {
      setShareCode(response);
    }
    setInviteLoading(false);
  };

  const handleCopy = () => {
    if (shareCode) {
      navigator.clipboard.writeText(shareCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  const handleInviteClose = () => {
    setInviteOpen(false);
    setShareCode(null);
    setInviteError(null);
    setInviteLoading(false);
    setCopied(false);
  };

  // Load available weeks from backend calendar; fallback to 1..15 on error.
  useEffect(() => {
    let cancelled = false;
    const loadWeeks = async () => {
      try {
        // Prefer backend current week to set a sensible default when unset
        if (week === undefined) {
          const currentResult = await getCurrentWeek();
          if (
            !(currentResult instanceof Error) &&
            !cancelled &&
            typeof currentResult?.week === "number" &&
            currentResult.week > 0
          ) {
            setWeek(currentResult.week);
          }
        }

        // Determine target year: if already set, reuse; else derive from current calendar response or system date
        const targetYear = year || new Date().getFullYear();
        if (!year) setYear(targetYear);
        const weeksResult = await getAvailableWeeks(targetYear);
        if (
          !(weeksResult instanceof Error) &&
          !cancelled &&
          weeksResult.length > 0
        ) {
          // Remove duplicates and sort
          const uniqueWeeks = weeksResult
            .filter((week, index) => weeksResult.indexOf(week) === index)
            .sort((a, b) => a - b);
          setAvailableWeeks(uniqueWeeks);
          return;
        }
        if (!cancelled && availableWeeks.length === 0) {
          const fallbackWeeks = Array.from({ length: 15 }, (_, i) => i + 1);
          setAvailableWeeks(fallbackWeeks);
        }
      } catch {
        // Fallback: seed 1..15 if API not available
        if (!cancelled && availableWeeks.length === 0) {
          const fallbackWeeks = Array.from({ length: 15 }, (_, i) => i + 1);
          setAvailableWeeks(fallbackWeeks);
        }
      }
    };
    loadWeeks();
    return () => {
      cancelled = true;
    };
  }, [getCurrentWeek, getAvailableWeeks, availableWeeks.length, week, year]);

  // Pick current week: prefer server-provided year/week from selected games; else current week; else first in availableWeeks
  useEffect(() => {
    if (week !== null) return;
    if (availableWeeks.length > 0) {
      setWeek(availableWeeks[0]);
    }
  }, [games, availableWeeks, week]);

  // Fetch polls and records data when year/week are available
  useEffect(() => {
    if (!year || !week) return;

    const fetchPollsAndRecords = async () => {
      if (!games || !games.games) {
        return;
      }

      const allTeamIds = Array.from(
        new Set([
          ...games.games.map((g: Game) => g.homeTeam),
          ...games.games.map((g: Game) => g.awayTeam),
        ])
      );

      if (allTeamIds.length === 0) {
        return;
      }

      // Fetch polls
      const pollsAndRecordsResponse = await getPollsAndRecords(
        year,
        week,
        allTeamIds
      );
      if (pollsAndRecordsResponse instanceof Error) {
        setError(`Error fetching polls: ${pollsAndRecordsResponse.message}`);
        setPollsData(null);
        setRecordsData(null);
        return;
      } else {
        setPollsData(pollsAndRecordsResponse.polls);
        setRecordsData(pollsAndRecordsResponse.records);
      }
    };

    fetchPollsAndRecords();
  }, [year, week, games, getPollsAndRecords]);

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(true);
        setError(null);

        if (week === undefined) return; // wait until week chosen
        if (year === undefined) return;

        const selectedGamesResponse = await getSelectedGames(
          leagueId,
          year,
          week
        );
        if (selectedGamesResponse instanceof Error) {
          setError(
            `Error fetching selected games: ${selectedGamesResponse.message}`
          );
          return;
        }

        // Map API SelectedGame (which includes metadata) into local selectedGame[] preserving metadata
        const mappedSelected: SelectedGame[] = (
          selectedGamesResponse.games || []
        ).map((sg: any) => ({
          game: sg.game,
          homeTeam: sg.homeTeam,
          awayTeam: sg.awayTeam,
          metadata: sg.metadata
            ? {
                id: sg.metadata.id ?? sg.game?.id,
                line:
                  typeof sg.metadata.line === "number"
                    ? sg.metadata.line
                    : undefined,
                flavorText: sg.metadata.flavorText || undefined,
              }
            : undefined,
        }));
        setSelectedGames(mappedSelected);
        setReady(selectedGamesResponse.ready);
        setGameOfTheWeek(selectedGamesResponse.gameOfTheWeek || undefined);

        const leagueResp = await getLeague(leagueId);
        if (leagueResp instanceof Error) {
          setCanEditControls(false);
        } else {
          const leagueData = leagueResp;
          const email = user?.email;
          const adminEmails: string[] = leagueData.admins.map(
            (u: User) => u.email
          );
          const canEdit =
            !!email &&
            (email === leagueData.owner.email || adminEmails.includes(email));
          setCanEditControls(canEdit);
        }

        if (selectedGamesResponse.ready) {
          return;
        }

        // Fetch classifications
        const classificationsResp = await getClassifications();
        if (classificationsResp instanceof Error) {
          setError(
            `Error fetching classifications: ${classificationsResp.message}`
          );
          return;
        } else {
          setClassifications(classificationsResp.classifications);
        }

        const conferencesResp = await getConferences(classification);
        if (conferencesResp instanceof Error) {
          setError(`Error fetching conferences: ${conferencesResp.message}`);
          return;
        } else {
          setConferences([allConferences, ...conferencesResp.conferences]);
        }

        // Fetch games and teams
        let conferenceField = conference ?? "";
        if (conference === allConferences) {
          conferenceField = "";
        }
        const gamesData = await getGames(
          classification,
          conferenceField,
          week,
          year
        );
        if (gamesData instanceof Error) {
          setError(`Error fetching games: ${gamesData.message}`);
          return;
        }

        // If there are no games, avoid subsequent requests and set empty teams map
        const teamsMapLocal = new Map<number, Team>();
        if (gamesData.games.length > 0) {
          // Prefer teams returned by the games endpoint (already filtered to the classification)
          if (gamesData.teams && typeof gamesData.teams === "object") {
            Object.entries(gamesData.teams).forEach(([key, value]) => {
              const idNum = Number(key);
              if (!Number.isNaN(idNum)) {
                teamsMapLocal.set(idNum, value as unknown as Team);
              }
            });
          } else {
            // Fallback: derive IDs and fetch teams if backend ever omits the teams map
            const ids = [
              ...gamesData.games.map((value) => value.homeTeam),
              ...gamesData.games.map((value) => value.awayTeam),
            ];
            if (ids.length > 0) {
              const teamsData = await getTeams(ids);
              if (teamsData instanceof Error) {
                setError(`Error fetching teams: ${teamsData.message}`);
                return;
              }
              teamsData.teams.forEach((value) => {
                teamsMapLocal.set(value.id, value);
              });
            }
          }
        }

        setGames({
          games: gamesData.games,
          teams: teamsMapLocal,
        });
      } catch (err: any) {
        setError(`Failed to fetch data: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [
    classification,
    conference,
    leagueId,
    callApi,
    user,
    week,
    year,
    getClassifications,
    getConferences,
    getLeague,
    getSelectedGames,
    getTeams,
    getGames,
  ]);

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(null);

    const response = await updateSelectedGames(
      leagueId,
      year ?? 0,
      week ?? 0,
      buildSelectedGamesPayload(false)
    );
    setSaving(false);
    if (response instanceof Error) {
      setSaveError(`Error updating selected games: ${response.message}`);
      setTimeout(() => setSaveError(null), 5000);
      return;
    }

    setSaveSuccess("Games saved successfully!");
    setTimeout(() => setSaveSuccess(null), 3000);
  };

  const handlePublish = async () => {
    if (publishing) return;
    if (showGameOfTheWeek && !gameOfTheWeek) {
      setPublishError("Game of the Week required before publishing.");
      setTimeout(() => setPublishError(null), 4000);
      return;
    }

    setPublishing(true);
    setPublishError(null);
    setPublishSuccess(null);

    const response = await updateSelectedGames(
      leagueId,
      year ?? 0,
      week ?? 0,
      buildSelectedGamesPayload(true)
    );
    setPublishing(false);
    if (response instanceof Error) {
      setPublishError(`Error publishing week: ${response.message}`);
      setTimeout(() => setPublishError(null), 6000);
      return;
    }

    setPublishSuccess("Week published! Picks are now open.");
    setReady(true);
    setTimeout(() => setPublishSuccess(null), 4000);
    setPublishConfirmOpen(false);
  };

  const canSubmit = () => {
    const hasRequiredGames = selectedGames.length === rules.gameCount;
    const hasGameOfTheWeek = !showGameOfTheWeek || gameOfTheWeek !== undefined;
    return hasRequiredGames && hasGameOfTheWeek;
  };

  const buildSelectedGamesPayload = (
    readyFlag: boolean
  ): UpdateSelectedGamesRequest => {
    return {
      games: selectedGames.map((g) => {
        const idNum = Number(g.game.id);
        // Prefer per-league custom line if present, else undefined so backend leaves nil
        const customLine = g.metadata?.line;
        // Only include flavorText if present; do not send blank string which would wipe existing
        const flavor = g.metadata?.flavorText;
        return {
          id: idNum,
          line: typeof customLine === "number" ? customLine : undefined,
          flavorText: flavor !== undefined ? flavor : undefined,
        };
      }),
      gameOfTheWeek:
        showGameOfTheWeek && gameOfTheWeek ? Number(gameOfTheWeek) : undefined,
      ready: readyFlag,
    };
  };

  const handleMetadataChange = (update: {
    id: string | number;
    line?: number;
    flavorText?: string;
  }) => {
    setSelectedGames((prev) =>
      prev.map((sg) => {
        if (String(sg.game.id) !== String(update.id)) return sg;
        const mergedMeta = {
          id: sg.metadata?.id || sg.game.id,
          line: update.line !== undefined ? update.line : sg.metadata?.line,
          flavorText:
            update.flavorText !== undefined
              ? update.flavorText
              : sg.metadata?.flavorText,
        };
        return { ...sg, metadata: mergedMeta };
      })
    );
  };

  // Reset transient state when week changes (excluding initial null)
  useEffect(() => {
    if (week === undefined) return;
    setSelectedGames([]);
    setGameOfTheWeek(undefined);
    setReady(false);
    setPage(0);
    setSelectedGamesPage(0);
  }, [week]);

  const showGameOfTheWeek = rules.gameOfTheWeekRules !== undefined;

  if (error) {
    return (
      <Box sx={{ padding: isMobile ? 2 : 3, maxWidth: 1200, margin: "0 auto" }}>
        <Paper
          elevation={2}
          sx={{ padding: isMobile ? 2 : 3, textAlign: "center" }}
        >
          <Typography
            variant={isMobile ? "subtitle1" : "h6"}
            color="error"
            sx={{ marginBottom: 2 }}
          >
            Error
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ fontSize: isMobile ? "0.875rem" : "inherit" }}
          >
            {error}
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (!games && !loading && !ready && availableWeeks.length > 0) {
    return (
      <Box sx={{ padding: isMobile ? 2 : 3, maxWidth: 1200, margin: "0 auto" }}>
        <Paper
          elevation={2}
          sx={{ padding: isMobile ? 2 : 3, textAlign: "center" }}
        >
          <Typography
            variant={isMobile ? "subtitle1" : "h6"}
            color="text.secondary"
            sx={{ marginBottom: 2 }}
          >
            No Data Available
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ fontSize: isMobile ? "0.875rem" : "inherit" }}
          >
            Couldn't fetch games. Please try refreshing the page.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Stack
      spacing={isMobile ? 2 : 3}
      sx={{ padding: isMobile ? 1 : 0, maxWidth: 1200, margin: "0 auto" }}
    >
      {/* Success/Error Alerts */}
      {saveSuccess && (
        <Alert
          severity="success"
          onClose={() => setSaveSuccess(null)}
          sx={{ fontSize: isMobile ? "0.875rem" : "inherit" }}
        >
          {saveSuccess}
        </Alert>
      )}
      {saveError && (
        <Alert
          severity="error"
          onClose={() => setSaveError(null)}
          sx={{ fontSize: isMobile ? "0.875rem" : "inherit" }}
        >
          {saveError}
        </Alert>
      )}
      {publishSuccess && (
        <Alert
          severity="success"
          onClose={() => setPublishSuccess(null)}
          sx={{ fontSize: isMobile ? "0.875rem" : "inherit" }}
        >
          {publishSuccess}
        </Alert>
      )}
      {publishError && (
        <Alert
          severity="error"
          onClose={() => setPublishError(null)}
          sx={{ fontSize: isMobile ? "0.875rem" : "inherit" }}
        >
          {publishError}
        </Alert>
      )}
      {selectError && (
        <Alert
          severity="warning"
          onClose={() => setSelectError(null)}
          sx={{ fontSize: isMobile ? "0.875rem" : "inherit" }}
        >
          {selectError}
        </Alert>
      )}
      {/* Header Controls */}
      <Paper elevation={2} sx={{ padding: isMobile ? 1 : 2 }}>
        <Stack
          direction={isMobile ? "column" : "row"}
          spacing={isMobile ? 1 : { xs: 1, sm: 2 }}
          alignItems={isMobile ? "stretch" : "center"}
          flexWrap="wrap"
          sx={{ gap: isMobile ? 1 : { xs: 1, sm: 2 } }}
        >
          <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
            <Button
              id="classification-button"
              variant="outlined"
              aria-controls={
                classificationOpen ? "classification-menu" : undefined
              }
              aria-haspopup="true"
              aria-expanded={classificationOpen ? "true" : undefined}
              onClick={handleClassificationClick}
              disabled={loading}
              sx={{
                minWidth: isMobile ? 100 : { xs: 120, sm: 150 },
                fontSize: isMobile ? "0.75rem" : "inherit",
              }}
            >
              {loading ? (
                <Skeleton variant="text" width={isMobile ? 40 : 60} />
              ) : (
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {classification.toUpperCase()}
                </Typography>
              )}
            </Button>
            <Menu
              id="classification-menu"
              anchorEl={classificationAnchorEl}
              open={classificationOpen}
              onClose={handleClassificationClose}
              MenuListProps={{
                "aria-labelledby": "basic-button",
              }}
            >
              {classifications
                .sort((a, b) => (a > b ? 1 : -1))
                .map((value, index) => {
                  return (
                    <MenuItem
                      key={index}
                      onClick={() => handleClassificationMenuItemClick(value)}
                    >
                      {value.toUpperCase()}
                    </MenuItem>
                  );
                })}
            </Menu>
            <Button
              id="conference-button"
              variant="outlined"
              aria-controls={conferenceOpen ? "conference-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={conferenceOpen ? "true" : undefined}
              onClick={handleConferenceClick}
              disabled={conferences.length === 0 || loading}
              sx={{
                minWidth: isMobile ? 100 : { xs: 120, sm: 150 },
                fontSize: isMobile ? "0.75rem" : "inherit",
              }}
            >
              {loading ? (
                <Skeleton variant="text" width={isMobile ? 60 : 80} />
              ) : (
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {conference === allConferences ||
                  conference === "" ||
                  conference === null
                    ? "ALL CONFERENCES"
                    : conference.toUpperCase()}
                </Typography>
              )}
            </Button>
            <Menu
              id="conference-menu"
              anchorEl={conferenceAnchorEl}
              open={conferenceOpen}
              onClose={handleConferenceClose}
              MenuListProps={{
                "aria-labelledby": "basic-button",
              }}
            >
              {conferences
                .sort((a, b) => (a > b ? 1 : -1))
                .map((value, index) => {
                  return (
                    <MenuItem
                      key={index}
                      onClick={() => handleConferenceMenuItemClick(value)}
                    >
                      {value === allConferences
                        ? "ALL CONFERENCES"
                        : value.toUpperCase()}
                    </MenuItem>
                  );
                })}
            </Menu>
            {/* Week dropdown */}
            <Button
              id="week-button"
              variant="outlined"
              aria-controls={weekOpen ? "week-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={weekOpen ? "true" : undefined}
              onClick={handleWeekClick}
              disabled={loading || availableWeeks.length === 0}
              sx={{
                minWidth: isMobile ? 60 : { xs: 80, sm: 100 },
                fontSize: isMobile ? "0.75rem" : "inherit",
              }}
            >
              {week ? (
                `WEEK ${week}`
              ) : loading ? (
                <Skeleton variant="text" width={isMobile ? 30 : 40} />
              ) : (
                "SET WEEK"
              )}
            </Button>
            <Menu
              id="week-menu"
              anchorEl={weekAnchorEl}
              open={weekOpen}
              onClose={handleWeekClose}
              MenuListProps={{ "aria-labelledby": "week-button" }}
            >
              {availableWeeks.map((w, index) => (
                <MenuItem
                  key={`week-${w}-${index}`}
                  selected={w === week}
                  onClick={() => {
                    setWeek(w);
                    handleWeekClose();
                  }}
                >
                  Week {w}
                </MenuItem>
              ))}
            </Menu>
          </Stack>
          <Stack
            direction="row"
            spacing={1}
            sx={{
              flexShrink: 0,
              justifyContent: isMobile ? "space-between" : "flex-end",
            }}
          >
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleInviteClick}
              size={isMobile ? "small" : "medium"}
              sx={{
                minWidth: isMobile ? 70 : { xs: 80, sm: 100 },
                fontSize: isMobile ? "0.75rem" : "inherit",
              }}
            >
              {isMobile ? "Invite" : "Invite Players"}
            </Button>
            {!ready && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleSave}
                disabled={saving || loading}
                startIcon={
                  saving ? <CircularProgress size={isMobile ? 14 : 16} /> : null
                }
                size={isMobile ? "small" : "medium"}
                sx={{
                  minWidth: isMobile ? 60 : { xs: 80, sm: 100 },
                  fontSize: isMobile ? "0.75rem" : "inherit",
                }}
              >
                {saving ? "Saving..." : "Save"}
              </Button>
            )}
            {!ready && (
              <Button
                variant="contained"
                color="success"
                onClick={() => setPublishConfirmOpen(true)}
                disabled={!canSubmit() || publishing || loading || ready}
                startIcon={
                  publishing ? (
                    <CircularProgress size={isMobile ? 14 : 16} />
                  ) : null
                }
                size={isMobile ? "small" : "medium"}
                sx={{
                  minWidth: isMobile ? 60 : { xs: 80, sm: 100 },
                  fontSize: isMobile ? "0.75rem" : "inherit",
                }}
              >
                {publishing ? "Publishing..." : "Publish"}
              </Button>
            )}
            {!ready && showGameOfTheWeek && !gameOfTheWeek && !loading && (
              <Typography
                variant="caption"
                sx={{
                  color: "warning.main",
                  fontSize: isMobile ? "0.6rem" : "0.7rem",
                  textAlign: "center",
                  display: "block",
                  marginTop: 0.5,
                }}
              >
                Select Game of the Week to publish
              </Typography>
            )}
          </Stack>
        </Stack>
      </Paper>
      {/* Selected Games Section */}
      <Paper elevation={2} sx={{ overflow: "hidden", mb: 4 }}>
        <Box
          sx={{
            background: "linear-gradient(135deg, #1976d2, #42a5f5)",
            color: "white",
            padding: isMobile ? 1.5 : 2,
          }}
        >
          <Typography
            variant={isMobile ? "subtitle1" : "h6"}
            sx={{ fontWeight: 600 }}
          >
            Selected Games ({selectedGames.length} of {rules.gameCount})
            {showGameOfTheWeek && !loading && (
              <Typography
                component="span"
                variant="body2"
                sx={{
                  display: "block",
                  fontWeight: 400,
                  opacity: 0.9,
                  marginTop: 0.5,
                }}
              >
                {gameOfTheWeek
                  ? "Game of the Week selected"
                  : "⚠️ Game of the Week required - click the trophy to select"}
              </Typography>
            )}
          </Typography>
          {/* Manual refresh removed; live polling keeps this updated */}
        </Box>
        <Box sx={{ padding: isMobile ? 1 : 2 }}>
          {loading ? (
            <SelectedGamesSkeleton
              count={Math.min(isMobile ? 12 : 24, rules.gameCount)}
            />
          ) : selectedGames.length === 0 ? (
            <Typography
              variant="body1"
              sx={{
                textAlign: "center",
                color: "#666",
                padding: isMobile ? 2 : 4,
              }}
            >
              No games selected yet. Click on games below to add them.
            </Typography>
          ) : (
            <Grid
              container
              spacing={isMobile ? 1.5 : 3}
              sx={{ marginBottom: 2 }}
            >
              {(() => {
                return sortGamesByStartStatus(
                  selectedGames,
                  (game) => game.game.time,
                  (game) => game.game.completed ?? false
                );
              })()
                .slice(
                  selectedGamesPage * rowsPerSelectedGamesPage,
                  (selectedGamesPage + 1) * rowsPerSelectedGamesPage
                )
                .map((value, index) => {
                  // Allow metadata edits (custom line / flavor) if user can edit AND (not yet published OR published but before kickoff)
                  let metadataEditable = false;
                  if (canEditControls) {
                    if (!ready) metadataEditable = true;
                    else {
                      const t = Date.parse(value.game.time);
                      if (!isNaN(t) && Date.now() < t) metadataEditable = true;
                    }
                  }
                  return (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                      <GameCard
                        game={value.game}
                        metadata={{
                          id: value.game.id,
                          line:
                            (value as any).metadata?.line ??
                            (value as any)?.metadata?.customLine ??
                            undefined,
                          flavorText:
                            (value as any).metadata?.flavorText ?? undefined,
                        }}
                        homeTeam={value.homeTeam}
                        awayTeam={value.awayTeam}
                        onClick={() => handleSelectedGamesRowClick(value)}
                        isSelected={true}
                        isGameOfTheWeek={value.game.id === gameOfTheWeek}
                        onGameOfTheWeekClick={
                          showGameOfTheWeek && !ready
                            ? () => handleGameOfTheWeekClick(value)
                            : undefined
                        }
                        showGameOfTheWeekButton={showGameOfTheWeek && !ready}
                        showPickIndicator={false}
                        showLines={Boolean(
                          rules.flavorLinesRules?.showGameLines !== false
                        )}
                        showScoringLine={Boolean(
                          rules.flavorLinesRules?.customGameLinesEnabled
                        )}
                        showLiveLine={
                          !(
                            rules.flavorLinesRules?.customGameLinesEnabled &&
                            rules.flavorLinesRules
                              ?.hideOfficialLinesWhenCustomEnabled
                          )
                        }
                        showCustomLine={Boolean(
                          rules.flavorLinesRules?.customGameLinesEnabled
                        )}
                        showFlavorText={Boolean(
                          rules.flavorLinesRules?.flavorTextEnabled
                        )}
                        enableEdit={metadataEditable}
                        editableLeagueId={leagueId}
                        leagueId={leagueId}
                        year={year}
                        week={week}
                        onMetadataChange={handleMetadataChange}
                        pushEnabled={Boolean(rules.pushEnabled)}
                        homeTeamPollRank={getTeamPollRank(
                          value.homeTeam.school,
                          pollsData
                        )}
                        awayTeamPollRank={getTeamPollRank(
                          value.awayTeam.school,
                          pollsData
                        )}
                        homeTeamRecord={getTeamRecord(
                          value.homeTeam.id,
                          recordsData
                        )}
                        awayTeamRecord={getTeamRecord(
                          value.awayTeam.id,
                          recordsData
                        )}
                      />
                    </Grid>
                  );
                })}
            </Grid>
          )}
        </Box>
        {selectedGames.length > 0 && (
          <Box
            sx={{ padding: isMobile ? 1 : 2, borderTop: "1px solid #e0e0e0" }}
          >
            <TablePagination
              rowsPerPageOptions={isMobile ? [12, 24] : [24, 48, 96]}
              component="div"
              count={selectedGames.length}
              rowsPerPage={rowsPerSelectedGamesPage}
              page={selectedGamesPage}
              onPageChange={handleChangeSelectedGamesPage}
              onRowsPerPageChange={handleChangeSelectedGamesRowsPerPage}
              size={isMobile ? "small" : "medium"}
            />
          </Box>
        )}
      </Paper>
      {/* Scheduled Games Section */}
      {!ready && (
        <Paper elevation={2} sx={{ overflow: "hidden", mb: 4 }}>
          <Box
            sx={{
              background: "linear-gradient(135deg, #2e7d32, #66bb6a)",
              color: "white",
              padding: isMobile ? 1.5 : 2,
            }}
          >
            <Typography
              variant={isMobile ? "subtitle1" : "h6"}
              sx={{ fontWeight: 600 }}
            >
              Scheduled Games {games && `(${games.games.length} total)`}
            </Typography>
          </Box>
          <Box sx={{ padding: isMobile ? 1 : 2 }}>
            {loading ? (
              <ScheduledGamesSkeleton count={rowsPerPage} />
            ) : games ? (
              <Grid
                container
                spacing={isMobile ? 1.5 : 3}
                sx={{ marginBottom: 2 }}
              >
                {sortGamesByStartStatus(
                  games.games,
                  (game) => game.time,
                  (game) => game.completed ?? false
                )
                  .slice(page * rowsPerPage, (page + 1) * rowsPerPage)
                  .map((value, index) => {
                    const homeTeam = games.teams.get(value.homeTeam);
                    const awayTeam = games.teams.get(value.awayTeam);
                    if (!homeTeam || !awayTeam) return null;

                    const isSelected = selectedGames.some(
                      (sg) => sg.game.id === value.id
                    );
                    const isGameOfTheWeek = Number(value.id) === gameOfTheWeek;

                    return (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                        <GameCard
                          game={value}
                          metadata={{
                            id: value.id,
                            line: undefined,
                            flavorText: undefined,
                          }}
                          homeTeam={homeTeam}
                          awayTeam={awayTeam}
                          onClick={() =>
                            handleRowClick(value, homeTeam, awayTeam)
                          }
                          isSelected={isSelected}
                          isGameOfTheWeek={isGameOfTheWeek}
                          onGameOfTheWeekClick={
                            showGameOfTheWeek && !ready
                              ? () =>
                                  handleGameOfTheWeekClick({
                                    game: value,
                                    homeTeam,
                                    awayTeam,
                                  })
                              : undefined
                          }
                          showGameOfTheWeekButton={showGameOfTheWeek && !ready}
                          showPickIndicator={false}
                          showLines={Boolean(
                            rules.flavorLinesRules?.showGameLines !== false
                          )}
                          showScoringLine={Boolean(
                            rules.flavorLinesRules?.customGameLinesEnabled
                          )}
                          showLiveLine={
                            !(
                              rules.flavorLinesRules?.customGameLinesEnabled &&
                              rules.flavorLinesRules
                                ?.hideOfficialLinesWhenCustomEnabled
                            )
                          }
                          showCustomLine={Boolean(
                            rules.flavorLinesRules?.customGameLinesEnabled
                          )}
                          showFlavorText={Boolean(
                            rules.flavorLinesRules?.flavorTextEnabled
                          )}
                          enableEdit={false}
                          editableLeagueId={leagueId}
                          pushEnabled={Boolean(rules.pushEnabled)}
                          year={year}
                          week={week}
                          homeTeamPollRank={getTeamPollRank(
                            homeTeam.school,
                            pollsData
                          )}
                          awayTeamPollRank={getTeamPollRank(
                            awayTeam.school,
                            pollsData
                          )}
                          homeTeamRecord={getTeamRecord(
                            homeTeam.id,
                            recordsData
                          )}
                          awayTeamRecord={getTeamRecord(
                            awayTeam.id,
                            recordsData
                          )}
                          currentUserEmail={userEmail}
                        />
                      </Grid>
                    );
                  })}
              </Grid>
            ) : (
              <Typography
                variant="body1"
                sx={{
                  textAlign: "center",
                  color: "#666",
                  padding: isMobile ? 2 : 4,
                }}
              >
                No games available.
              </Typography>
            )}
          </Box>
          {!loading && games && (
            <Box
              sx={{ padding: isMobile ? 1 : 2, borderTop: "1px solid #e0e0e0" }}
            >
              <TablePagination
                rowsPerPageOptions={isMobile ? [12, 24] : [24, 48, 96]}
                component="div"
                count={games.games.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                size={isMobile ? "small" : "medium"}
              />
            </Box>
          )}
        </Paper>
      )}
      <Dialog open={inviteOpen} onClose={handleInviteClose}>
        <DialogTitle>Invite Players</DialogTitle>
        <DialogContent>
          {inviteLoading ? (
            <CircularProgress />
          ) : inviteError ? (
            <Alert severity="error">{inviteError}</Alert>
          ) : shareCode ? (
            <>
              <TextField
                label="Share Code"
                value={shareCode}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleCopy} edge="end">
                        <ContentCopyIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                  readOnly: true,
                }}
                sx={{ mt: 2 }}
              />
              <TextField
                label="Share Link"
                value={`${window.location.origin}/join?code=${shareCode}`}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={async () => {
                          const shareUrl = `${window.location.origin}/join?code=${shareCode}`;
                          if (navigator.share) {
                            try {
                              await navigator.share({
                                title: "Join my Pickem League!",
                                text: "Join my Pickem League using this link:",
                                url: shareUrl,
                              });
                              setCopied(true);
                              setTimeout(() => setCopied(false), 1500);
                            } catch (err) {
                              navigator.clipboard.writeText(shareUrl);
                              setCopied(true);
                              setTimeout(() => setCopied(false), 1500);
                            }
                          } else {
                            navigator.clipboard.writeText(shareUrl);
                            setCopied(true);
                            setTimeout(() => setCopied(false), 1500);
                          }
                        }}
                        edge="end"
                      >
                        {typeof window !== "undefined" &&
                        typeof navigator.share === "function" ? (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            height="24"
                            viewBox="0 0 24 24"
                            width="24"
                          >
                            <path d="M0 0h24v24H0z" fill="none" />
                            <path
                              d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.03-.47-.09-.7l7.02-4.11c.53.5 1.23.81 2.01.81 1.66 0 
3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.91 9.81C8.38 9.31 7.68 9 6.9 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.78 0 1.48-.31 2.01-.81l7.12 4.15c-.05.21-.08.43-.08.66 0 1.52 1.23 2.75 2.75 2.75s2.75-1.23 2.75-2.75-1.23-2.75-2.75-2.75z"
                            />{" "}
                          </svg>
                        ) : (
                          <ContentCopyIcon />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                  readOnly: true,
                }}
                sx={{ mt: 2 }}
              />
              <Button
                variant="outlined"
                fullWidth
                sx={{ mt: 1 }}
                onClick={async () => {
                  const shareUrl = `${window.location.origin}/join?code=${shareCode}`;
                  if (navigator.share) {
                    try {
                      await navigator.share({
                        title: "Join my Pickem League!",
                        text: "Join my Pickem League using this link:",
                        url: shareUrl,
                      });
                      setCopied(true);
                      setTimeout(() => setCopied(false), 1500);
                    } catch (err) {
                      // User cancelled or error, fallback to clipboard
                      navigator.clipboard.writeText(shareUrl);
                      setCopied(true);
                      setTimeout(() => setCopied(false), 1500);
                    }
                  } else {
                    navigator.clipboard.writeText(shareUrl);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 1500);
                  }
                }}
              >
                Share Invite Link
              </Button>
            </>
          ) : null}
          {copied && (
            <Alert severity="success" sx={{ mt: 2 }}>
              Copied!
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleInviteClose}>Close</Button>
        </DialogActions>
      </Dialog>
      {/* Publish confirmation dialog */}
      <Dialog
        open={publishConfirmOpen}
        onClose={() => setPublishConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Publish Week?</DialogTitle>
        <DialogContent>
          <Stack spacing={1} sx={{ mt: 1 }}>
            <Typography variant="body2">
              You are about to publish this week with {selectedGames.length}{" "}
              game
              {selectedGames.length === 1 ? "" : "s"}
              {showGameOfTheWeek && gameOfTheWeek
                ? " (Game of the Week set)"
                : showGameOfTheWeek
                ? ", but no Game of the Week set"
                : ""}
              .
            </Typography>
            <Typography variant="body2">
              After publishing, you cannot add/remove games or change the Game
              of the Week. You may still edit custom lines and flavor text up
              until each game kicks off.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setPublishConfirmOpen(false)}
            disabled={publishing}
          >
            Cancel
          </Button>
          <Button
            onClick={handlePublish}
            variant="contained"
            color="success"
            disabled={publishing || !canSubmit()}
            startIcon={publishing ? <CircularProgress size={16} /> : undefined}
          >
            {publishing ? "Publishing..." : "Publish"}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};
export default Admin;
